import type {
  PlanningList,
  CreatePlanningListInput,
  UpdatePlanningListInput,
  ClonePlanningListInput,
  SavePlanningListAsTemplateInput,
  PlanningItem,
  CreatePlanningItemInput,
  UpdatePlanningItemInput,
  PlanningTodo,
  CreatePlanningTodoInput,
  UpdatePlanningTodoInput,
  PlanningTodoWithContext,
  PlanningStatus,
  CreatePlanningStatusInput,
  UpdatePlanningStatusInput,
} from '@peles/shared';
import { apiClient } from './client';

// ─── Planning Lists ───────────────────────────────────────────────────────────

export async function getPlanningLists(filters?: {
  eventId?: string;
  isTemplate?: boolean;
  eventTypeId?: string;
}): Promise<PlanningList[]> {
  const params: Record<string, string> = {};
  if (filters?.eventId) params.eventId = filters.eventId;
  if (filters?.isTemplate !== undefined) params.isTemplate = String(filters.isTemplate);
  if (filters?.eventTypeId) params.eventTypeId = filters.eventTypeId;
  const { data } = await apiClient.get<PlanningList[]>('/planning/lists', { params });
  return data;
}

export async function getPlanningList(id: string): Promise<PlanningList> {
  const { data } = await apiClient.get<PlanningList>(`/planning/lists/${id}`);
  return data;
}

export async function createPlanningList(input: CreatePlanningListInput): Promise<PlanningList> {
  const { data } = await apiClient.post<PlanningList>('/planning/lists', input);
  return data;
}

export async function updatePlanningList(
  id: string,
  updates: UpdatePlanningListInput,
): Promise<PlanningList> {
  const { data } = await apiClient.patch<PlanningList>(`/planning/lists/${id}`, updates);
  return data;
}

export async function deletePlanningList(id: string): Promise<void> {
  await apiClient.delete(`/planning/lists/${id}`);
}

export async function clonePlanningList(
  id: string,
  body: ClonePlanningListInput,
): Promise<PlanningList> {
  const { data } = await apiClient.post<PlanningList>(`/planning/lists/${id}/clone`, body);
  return data;
}

export async function savePlanningListAsTemplate(
  id: string,
  body: SavePlanningListAsTemplateInput,
): Promise<PlanningList> {
  const { data } = await apiClient.post<PlanningList>(
    `/planning/lists/${id}/save-as-template`,
    body,
  );
  return data;
}

export async function archivePlanningList(id: string): Promise<PlanningList> {
  const { data } = await apiClient.patch<PlanningList>(`/planning/lists/${id}/archive`, {});
  return data;
}

export async function getPlanningTemplates(filters?: {
  eventTypeId?: string;
  includeArchived?: boolean;
}): Promise<PlanningList[]> {
  const params: Record<string, string> = {};
  if (filters?.eventTypeId) params.eventTypeId = filters.eventTypeId;
  if (filters?.includeArchived !== undefined) {
    params.includeArchived = String(filters.includeArchived);
  }
  const { data } = await apiClient.get<PlanningList[]>('/planning/templates', { params });
  return data;
}

// ─── Planning Items ───────────────────────────────────────────────────────────

export async function addPlanningItem(
  listId: string,
  input: CreatePlanningItemInput,
): Promise<PlanningItem> {
  const { data } = await apiClient.post<PlanningItem>(`/planning/lists/${listId}/items`, input);
  return data;
}

export async function updatePlanningItem(
  itemId: string,
  updates: UpdatePlanningItemInput,
): Promise<PlanningItem> {
  const { data } = await apiClient.patch<PlanningItem>(`/planning/items/${itemId}`, updates);
  return data;
}

export async function deletePlanningItem(itemId: string): Promise<void> {
  await apiClient.delete(`/planning/items/${itemId}`);
}

// ─── Planning Todos ───────────────────────────────────────────────────────────

export async function addPlanningTodo(
  itemId: string,
  input: CreatePlanningTodoInput,
): Promise<PlanningTodo> {
  const { data } = await apiClient.post<PlanningTodo>(`/planning/items/${itemId}/todos`, input);
  return data;
}

export async function updatePlanningTodo(
  todoId: string,
  updates: UpdatePlanningTodoInput,
): Promise<PlanningTodo> {
  const { data } = await apiClient.patch<PlanningTodo>(`/planning/todos/${todoId}`, updates);
  return data;
}

export async function deletePlanningTodo(todoId: string): Promise<void> {
  await apiClient.delete(`/planning/todos/${todoId}`);
}

export interface PlanningTodosForCalendarResult {
  items: PlanningTodoWithContext[];
  overdueItems: PlanningTodoWithContext[];
}

export async function getPlanningTodosForCalendar(params: {
  dateFrom?: string;
  dateTo?: string;
}): Promise<PlanningTodosForCalendarResult> {
  const { data } = await apiClient.get<PlanningTodosForCalendarResult>('/planning/todos/calendar', {
    params,
  });
  return data;
}

// ─── Planning Statuses (Settings) ───────────────────────────────────────────────

export async function getPlanningStatuses(): Promise<PlanningStatus[]> {
  const { data } = await apiClient.get<PlanningStatus[]>('/settings/planning-statuses');
  return data;
}

export async function createPlanningStatus(
  input: CreatePlanningStatusInput,
): Promise<PlanningStatus> {
  const { data } = await apiClient.post<PlanningStatus>('/settings/planning-statuses', input);
  return data;
}

export async function updatePlanningStatus(
  id: string,
  updates: UpdatePlanningStatusInput,
): Promise<PlanningStatus> {
  const { data } = await apiClient.patch<PlanningStatus>(
    `/settings/planning-statuses/${id}`,
    updates,
  );
  return data;
}
